'use client';

import React from 'react';
import styled from 'styled-components';
import Button1 from './Button1';
import { COLORS } from '@/constants/colors';

const BENEFITS = [
  {
    id: 0,
    title: 'Improved Airflow',
    description:
      'Green Filter\'s high-flow design helps your engine breathe more freely compared to a restrictive stock paper filter. Better airflow can support stronger throttle response and improved performance.',
    imgSrc: '/assets/gear-wheel-3109067.webp',
  },
  {
    id: 1,
    title: 'Reusable and Washable',
    description:
      'Instead of throwing away your air filter every few thousand miles, a Green Filter can be cleaned, re-oiled, and reused. This makes it a smart long-term option for drivers who want performance and value.',
    imgSrc: '/assets/Asset-1goshadowicon.webp',
  },
  {
    id: 2,
    title: 'Made in the USA',
    description:
      'Green Filters are made in Uniontown, Pennsylvania, using quality materials and careful manufacturing processes. Every filter is built with attention to fitment, durability, and performance.',
    imgSrc: '/assets/usa-1177272.webp',
  },
  {
    id: 3,
    title: 'Eco-Friendly Alternative',
    description:
      'Because Green Filters are washable and reusable, they help reduce the need for disposable paper filter replacements. That means less waste over the life of your vehicle.',
    imgSrc: '/assets/green-filter-icon.webp',
  },
];

interface HomeElement4Props {
  text?: string;
}

const HomeElement4: React.FC<HomeElement4Props> = ({ text }) => {
  const defaultText = "A Green Filter is more than a simple replacement air filter. It is a washable, reusable air intake filter designed to improve airflow, support engine performance, and reduce the waste created by disposable paper filters.";

  return (
    <Section>
      <Container>
        <HeadingBlock>
          <Headline>Better Airflow. Long-Term Value. Lifetime Performance.</Headline>
          <Accent />
          <Subtitle>{text || defaultText}</Subtitle>
        </HeadingBlock>

        <BenefitsGrid>
          {BENEFITS.map((benefit) => (
            <BenefitCard key={benefit.id}>
              <IconContainer>
                <Icon src={benefit.imgSrc} alt={benefit.title} />
              </IconContainer>
              <CardTitle>{benefit.title}</CardTitle>
              <CardText>{benefit.description}</CardText>
            </BenefitCard>
          ))}
        </BenefitsGrid>

        <ButtonWrapper>
          <Button1 text="Find Your Green Filter" isLink={true} to="/shop-by-vehicle" />
        </ButtonWrapper>
      </Container>
    </Section>
  );
};

export default React.memo(HomeElement4);

const Section = styled.section`
  background: #fff;
  padding: 80px 24px;

  @media (max-width: 1024px) {
    padding: 64px 24px;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const Headline = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 0 auto 16px;
`;

const Subtitle = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #666;
  margin: 0 0 24px;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 15px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 48px;
  text-align: center;

  @media (max-width: 767px) {
    margin-top: 36px;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const BenefitCard = styled.div`
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  overflow: hidden;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${COLORS.brandGreen};
  }
`;

const IconContainer = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  padding: 24px;
  transition: all 0.3s ease;

  ${BenefitCard}:hover & {
    background: #e8f5e9;
  }

  @media (max-width: 767px) {
    height: 160px;
    padding: 20px;
  }
`;

const Icon = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
`;

const CardTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px;
  line-height: 1.3;
  padding: 24px 24px 0;

  @media (max-width: 767px) {
    font-size: 18px;
    padding: 20px 20px 0;
  }
`;

const CardText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #555;
  margin: 0;
  line-height: 1.6;
  padding: 0 24px 24px;

  @media (max-width: 767px) {
    font-size: 13px;
    padding: 0 20px 20px;
  }
`;
